import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ImageUpload from "../../components/ImageUpload";
import { getSports, createSport, updateSport, deleteSport, type PlatformSport } from "../../services/sports";
import {
  getLifePackages,
  createLifePackage,
  updateLifePackage,
  deleteLifePackage,
  type LivePackage,
} from "../../services/livesPurchase";
import api from "../../services/api";
import SupportEmailsField from "../../components/admin/SupportEmailsField";
import { clearFeatureCache } from "../../hooks/useFeatures";
import StripeStatusPanel from "../../components/admin/StripeStatusPanel";

// Platform settings type
interface PlatformSettings {
  id: string;
  platform_name: string;
  platform_logo: string | null;
  favicon_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  support_notification_emails?: string[] | null;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  meta_og_image: string | null;
  // Company details
  company_legal_name: string | null;
  company_cui: string | null;
  company_reg_number: string | null;
  company_address: string | null;
  company_bank_account: string | null;
  company_bank_name: string | null;
  // Invoice settings
  invoice_prefix: string;
  invoice_proforma_prefix: string;
  invoice_suffix: string | null;
  invoice_next_number: number;
  invoice_vat_percent: number;
  company_vat_payer: boolean | null;
  /// The secret itself is never returned — only whether one is stored.
  oblio_secret_set?: boolean;
  oblio_enabled: boolean;
  oblio_email: string;
  oblio_cif: string;
  oblio_series_name: string;
  oblio_vat_name: string;
  oblio_work_station: string;
  oblio_secret: string;
  invoice_payment_days: number;
  // Demo limits
  demo_max_clubs: number;
  demo_max_coaches: number;
  demo_max_athletes: number;
  stripe_mode: string;
  lives_purchase_enabled?: boolean;
  life_price_cents?: number;
  lives_currency?: string;
  brevo_api_key: string | null;
  brevo_sender_email: string | null;
  brevo_sender_name: string | null;
  payments_module_enabled?: boolean;
  payments_online_enabled?: boolean;
  payments_reminders_enabled?: boolean;
  payments_auto_debit_enabled?: boolean;
  payments_discounts_enabled?: boolean;
  payments_instalments_enabled?: boolean;
}

export default function Settings() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"general" | "company" | "email" | "integrations" | "security" | "sports" | "invoices" | "demo" | "payments">("general");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  // The parent-payments module's switches. Off until the platform says
  // otherwise, which is also what the server answers while nobody has decided.
  const [paymentsForm, setPaymentsForm] = useState({
    payments_module_enabled: false,
    payments_online_enabled: false,
    payments_reminders_enabled: false,
    payments_auto_debit_enabled: false,
    payments_discounts_enabled: false,
    payments_instalments_enabled: false,
  });

  const [emailTestLoading, setEmailTestLoading] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Sports management state
  const [sports, setSports] = useState<PlatformSport[]>([]);
  const [loadingSports, setLoadingSports] = useState(false);
  const [showAddSportModal, setShowAddSportModal] = useState(false);
  const [showEditSportModal, setShowEditSportModal] = useState(false);
  const [showDeleteSportModal, setShowDeleteSportModal] = useState(false);
  const [selectedSport, setSelectedSport] = useState<PlatformSport | null>(null);
  const [sportFormData, setSportFormData] = useState({
    name: "",
    icon: "",
    is_active: true,
  });

  // General settings
  const [generalForm, setGeneralForm] = useState({
    platform_name: "iGROWth",
    platform_logo: "",
    favicon_url: "",
    contact_email: "contact@igrowth.ro",
    contact_phone: "+40 123 456 789",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    meta_og_image: "",
  });

  // Where support notifications land. Separate from `contact_email`, which is
  // the address shown TO users; this one is the one we write to.
  const [supportEmails, setSupportEmails] = useState<string[]>([]);

  // Email settings - Brevo (formerly Sendinblue)
  const [emailForm, setEmailForm] = useState({
    brevo_api_key: "",
    sender_email: "noreply@igrowth.ro",
    sender_name: "iGROWth",
  });
  const [hasBrevoKey, setHasBrevoKey] = useState(false);

  // Company details (for invoices)
  const [companyForm, setCompanyForm] = useState({
    company_legal_name: "",
    company_cui: "",
    company_reg_number: "",
    company_address: "",
    company_bank_account: "",
    company_bank_name: "",
  });

  // Invoice settings
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_prefix: "IGR",
    invoice_proforma_prefix: "PRO",
    invoice_suffix: "",
    invoice_next_number: 1,
    invoice_vat_percent: 0,
    company_vat_payer: null as boolean | null,
    oblio_enabled: false,
    oblio_email: "",
    oblio_cif: "",
    oblio_series_name: "",
    oblio_vat_name: "",
    oblio_work_station: "",
    // Never populated from the server; empty means "leave the stored one".
    oblio_secret: "",
    invoice_payment_days: 5,
  });
  const [isResettingInvoice, setIsResettingInvoice] = useState(false);

  // Integrations settings
  // No Stripe keys here, on purpose: they live in the server's environment
  // (Railway), the server reads them from there, and a secret key stored in a
  // table a hundred admin screens can read is a secret in name only. What this
  // page shows about Stripe is read back from the server — see
  // StripeStatusPanel.
  const [integrationsForm, setIntegrationsForm] = useState({
    lives_purchase_enabled: false,
    life_price_cents: 0,
    lives_currency: "ron",
  });

  // Life packages (admin CRUD)
  const [lifePackages, setLifePackages] = useState<LivePackage[]>([]);
  const loadLifePackages = async () => {
    try {
      setLifePackages(await getLifePackages());
    } catch {
      /* not platform admin / not available */
    }
  };
  useEffect(() => {
    loadLifePackages();
  }, []);
  const patchPkgLocal = (id: string, patch: Partial<LivePackage>) =>
    setLifePackages((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const savePackage = async (p: LivePackage) => {
    try {
      await updateLifePackage(p.id, {
        qty: p.qty,
        price_cents: p.price_cents,
        active: p.active,
        sort_order: p.sort_order,
      });
      await loadLifePackages();
    } catch {
      alert("Nu am putut salva pachetul.");
    }
  };
  const addPackage = async () => {
    try {
      await createLifePackage({ qty: 5, price_cents: 500, currency: "ron", active: true, sort_order: lifePackages.length + 1 });
      await loadLifePackages();
    } catch {
      alert("Nu am putut adăuga pachetul.");
    }
  };
  const removePackage = async (id: string) => {
    if (!window.confirm("Ștergi acest pachet?")) return;
    try {
      await deleteLifePackage(id);
      await loadLifePackages();
    } catch {
      alert("Nu am putut șterge pachetul.");
    }
  };

  // Test Brevo email connection
  const testEmailConnection = async () => {
    setEmailTestLoading(true);
    setEmailTestResult(null);
    try {
      // Mock test - in production, this would call the backend API
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (emailForm.brevo_api_key && emailForm.brevo_api_key.length > 20) {
        setEmailTestResult({ success: true, message: t("settings.email_connection_success", "Conexiune reușită!") });
      } else {
        setEmailTestResult({ success: false, message: t("settings.email_invalid_key", "Cheie API invalidă") });
      }
    } catch (error) {
      setEmailTestResult({ success: false, message: t("settings.email_connection_failed", "Conexiune eșuată") });
    } finally {
      setEmailTestLoading(false);
    }
  };

  // Get webhook URL
  const getWebhookUrl = () => {
    // Use the backend URL (Railway) instead of frontend URL
    const backendUrl = process.env.REACT_APP_API_URL || "https://igapp-production.up.railway.app";
    return `${backendUrl}/webhooks/stripe`;
  };

  // Security settings
  const [securityForm, setSecurityForm] = useState({
    password_min_length: 8,
    require_special_char: true,
    session_timeout: 120,
  });

  // Demo account limits
  const [demoForm, setDemoForm] = useState({
    demo_max_clubs: 1,
    demo_max_coaches: 3,
    demo_max_athletes: 30,
  });

  // Server-derived, never round-tripped through the form: the secret itself is
  // not sent back, only whether one is stored.
  const [oblioSecretSet, setOblioSecretSet] = useState(false);
  const [oblioTesting, setOblioTesting] = useState(false);
  const [oblioCheck, setOblioCheck] = useState<any>(null);

  // Show toast helper
  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  /**
   * Verify the STORED credentials and pull the account's series and VAT rate
   * names, so those two fields can be picked instead of typed — a name Oblio
   * does not recognise is rejected only at the first real invoice otherwise.
   */
  const testOblio = async () => {
    setOblioTesting(true);
    setOblioCheck(null);
    try {
      const { data } = await api.post("/invoices/oblio/test");
      setOblioCheck(data);
      showToast("success", "Conexiunea la Oblio funcționează.");
    } catch (e: any) {
      showToast("error", e?.response?.data?.error || "Nu am putut contacta Oblio.");
    }
    setOblioTesting(false);
  };

  // Load platform settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoadingSettings(true);
        const { data } = await api.get<PlatformSettings>("/platform-settings");

        // Populate forms with loaded data
        setGeneralForm({
          platform_name: data.platform_name || "iGROWth",
          platform_logo: data.platform_logo || "",
          favicon_url: data.favicon_url || "",
          contact_email: data.contact_email || "",
          contact_phone: data.contact_phone || "",
          meta_title: data.meta_title || "",
          meta_description: data.meta_description || "",
          meta_keywords: data.meta_keywords || "",
          meta_og_image: data.meta_og_image || "",
        });

        setSupportEmails(data.support_notification_emails || []);

        setPaymentsForm({
          payments_module_enabled: !!data.payments_module_enabled,
          payments_online_enabled: !!data.payments_online_enabled,
          payments_reminders_enabled: !!data.payments_reminders_enabled,
          payments_auto_debit_enabled: !!data.payments_auto_debit_enabled,
          payments_discounts_enabled: !!data.payments_discounts_enabled,
          payments_instalments_enabled: !!data.payments_instalments_enabled,
        });

        // Show actual API key so user can verify it's saved correctly
        const keyExists = !!(data.brevo_api_key && data.brevo_api_key.length > 10);
        setHasBrevoKey(keyExists);
        setEmailForm({
          brevo_api_key: data.brevo_api_key || "", // Show actual key
          sender_email: data.brevo_sender_email || "",
          sender_name: data.brevo_sender_name || "",
        });

        setCompanyForm({
          company_legal_name: data.company_legal_name || "",
          company_cui: data.company_cui || "",
          company_reg_number: data.company_reg_number || "",
          company_address: data.company_address || "",
          company_bank_account: data.company_bank_account || "",
          company_bank_name: data.company_bank_name || "",
        });

        setOblioSecretSet(!!data.oblio_secret_set);
        setInvoiceForm({
          invoice_prefix: data.invoice_prefix || "IGR",
          invoice_proforma_prefix: data.invoice_proforma_prefix || "PRO",
          invoice_suffix: data.invoice_suffix || "",
          invoice_next_number: data.invoice_next_number || 1,
          invoice_vat_percent: data.invoice_vat_percent || 0,
          company_vat_payer: data.company_vat_payer ?? null,
          oblio_enabled: !!data.oblio_enabled,
          oblio_email: data.oblio_email || "",
          oblio_cif: data.oblio_cif || "",
          oblio_series_name: data.oblio_series_name || "",
          oblio_vat_name: data.oblio_vat_name || "",
          oblio_work_station: data.oblio_work_station || "",
          oblio_secret: "",
          invoice_payment_days: data.invoice_payment_days || 5,
        });

        setDemoForm({
          demo_max_clubs: data.demo_max_clubs ?? 1,
          demo_max_coaches: data.demo_max_coaches ?? 3,
          demo_max_athletes: data.demo_max_athletes ?? 30,
        });

        setIntegrationsForm(prev => ({
          ...prev,
          lives_purchase_enabled: !!data.lives_purchase_enabled,
          life_price_cents: data.life_price_cents ?? 0,
          lives_currency: data.lives_currency || "ron",
        }));

        setSecurityForm({
          password_min_length: (data as any).password_min_length ?? 8,
          require_special_char: (data as any).require_special_char ?? false,
          session_timeout: (data as any).session_timeout ?? 120,
        });
      } catch (error: any) {
        console.error("Failed to load settings:", error);
        // Only show error if it's not a 403 (user might not be platform admin)
        if (error?.response?.status !== 403) {
          showToast("error", t("settings.load_failed", "Nu s-au putut încărca setările"));
        }
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, [t]);

  // Load sports when Sports tab is active
  useEffect(() => {
    if (activeTab === "sports") {
      loadSports();
    }
  }, [activeTab]);

  const loadSports = async () => {
    try {
      setLoadingSports(true);
      const data = await getSports(true); // Load all sports including inactive
      setSports(data);
    } catch (err) {
      console.error("Failed to load sports:", err);
    } finally {
      setLoadingSports(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Build the settings payload based on active tab
      const payload: Record<string, any> = {};

      // General settings
      payload.platform_name = generalForm.platform_name;
      payload.platform_logo = generalForm.platform_logo || null;
      payload.favicon_url = generalForm.favicon_url || null;
      payload.contact_email = generalForm.contact_email || null;
      payload.contact_phone = generalForm.contact_phone || null;
      payload.meta_title = generalForm.meta_title || null;
      payload.meta_description = generalForm.meta_description || null;
      payload.meta_keywords = generalForm.meta_keywords || null;
      payload.meta_og_image = generalForm.meta_og_image || null;
      payload.support_notification_emails = supportEmails;

      // Email/Brevo settings - only send API key if user entered a new one
      if (emailForm.brevo_api_key) {
        payload.brevo_api_key = emailForm.brevo_api_key;
      }
      // Don't send null if key exists and user didn't enter new one
      payload.brevo_sender_email = emailForm.sender_email || null;
      payload.brevo_sender_name = emailForm.sender_name || null;

      // Company details
      payload.company_legal_name = companyForm.company_legal_name || null;
      payload.company_cui = companyForm.company_cui || null;
      payload.company_reg_number = companyForm.company_reg_number || null;
      payload.company_address = companyForm.company_address || null;
      payload.company_bank_account = companyForm.company_bank_account || null;
      payload.company_bank_name = companyForm.company_bank_name || null;

      // Invoice settings
      payload.invoice_prefix = invoiceForm.invoice_prefix || "IGR";
      payload.invoice_proforma_prefix = invoiceForm.invoice_proforma_prefix || "PRO";
      payload.invoice_suffix = invoiceForm.invoice_suffix || null;
      payload.invoice_vat_percent = invoiceForm.invoice_vat_percent;
      payload.company_vat_payer = invoiceForm.company_vat_payer;
      payload.oblio_enabled = invoiceForm.oblio_enabled;
      payload.oblio_email = invoiceForm.oblio_email;
      payload.oblio_cif = invoiceForm.oblio_cif;
      payload.oblio_series_name = invoiceForm.oblio_series_name;
      payload.oblio_vat_name = invoiceForm.oblio_vat_name;
      payload.oblio_work_station = invoiceForm.oblio_work_station;
      // Only sent when retyped; the backend treats empty as "keep the stored one".
      if (invoiceForm.oblio_secret) payload.oblio_secret = invoiceForm.oblio_secret;
      payload.invoice_payment_days = invoiceForm.invoice_payment_days;
      // Note: invoice_next_number is managed separately via reset endpoint

      // Demo limits
      payload.demo_max_clubs = demoForm.demo_max_clubs;
      payload.demo_max_coaches = demoForm.demo_max_coaches;
      payload.demo_max_athletes = demoForm.demo_max_athletes;

      // Lives purchase
      payload.lives_purchase_enabled = integrationsForm.lives_purchase_enabled;
      payload.life_price_cents = integrationsForm.life_price_cents;
      payload.lives_currency = integrationsForm.lives_currency;

      // Security policy
      payload.password_min_length = securityForm.password_min_length;
      payload.require_special_char = securityForm.require_special_char;
      payload.session_timeout = securityForm.session_timeout;

      // Parent-payments module
      Object.assign(payload, paymentsForm);

      await api.patch("/platform-settings", payload);

      // Every screen reads these flags, so the cached answer has to go or the
      // admin saves the switch and the app carries on as before.
      clearFeatureCache();

      // Update hasBrevoKey if we just saved a new key
      if (emailForm.brevo_api_key) {
        setHasBrevoKey(true);
        // Don't clear the key - user wants to see what was saved
      }

      showToast("success", t("settings.saved", "Setări salvate cu succes!"));
    } catch (error: any) {
      console.error("Failed to save settings:", error);
      showToast("error", error?.response?.data?.error || t("settings.save_failed", "Nu s-au putut salva setările"));
    } finally {
      setIsSaving(false);
    }
  };

  // Reset invoice number
  const handleResetInvoiceNumber = async () => {
    if (!window.confirm(t("settings.reset_invoice_confirm", "Sigur vrei să resetezi numărul facturii? Acest lucru va afecta toate facturile viitoare."))) {
      return;
    }

    setIsResettingInvoice(true);
    try {
      await api.post("/platform-settings/reset-invoice-number", {
        new_number: invoiceForm.invoice_next_number,
      });
      showToast("success", t("settings.invoice_reset_success", "Număr factură resetat cu succes!"));
    } catch (error: any) {
      console.error("Failed to reset invoice number:", error);
      showToast("error", error?.response?.data?.error || t("settings.invoice_reset_failed", "Nu s-a putut reseta numărul facturii"));
    } finally {
      setIsResettingInvoice(false);
    }
  };

  const handleAddSport = async () => {
    try {
      await createSport(sportFormData);
      setShowAddSportModal(false);
      setSportFormData({ name: "", icon: "", is_active: true });
      loadSports();
    } catch (err) {
      console.error("Failed to create sport:", err);
      alert("Eroare la crearea sportului");
    }
  };

  const handleEditSport = async () => {
    if (!selectedSport) return;
    try {
      await updateSport(selectedSport.id, sportFormData);
      setShowEditSportModal(false);
      setSelectedSport(null);
      setSportFormData({ name: "", icon: "", is_active: true });
      loadSports();
    } catch (err) {
      console.error("Failed to update sport:", err);
      alert("Eroare la actualizarea sportului");
    }
  };

  const handleDeleteSport = async () => {
    if (!selectedSport) return;
    try {
      await deleteSport(selectedSport.id);
      setShowDeleteSportModal(false);
      setSelectedSport(null);
      loadSports();
    } catch (err) {
      console.error("Failed to delete sport:", err);
      alert("Eroare la ștergerea sportului");
    }
  };

  const openEditSportModal = (sport: PlatformSport) => {
    setSelectedSport(sport);
    setSportFormData({
      name: sport.name,
      icon: sport.icon || "",
      is_active: sport.is_active,
    });
    setShowEditSportModal(true);
  };

  const openDeleteSportModal = (sport: PlatformSport) => {
    setSelectedSport(sport);
    setShowDeleteSportModal(true);
  };

  const tabs = [
    { id: "general" as const, label: t("settings.general"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )},
    { id: "company" as const, label: t("settings.company", "Companie"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    )},
    { id: "sports" as const, label: "Sporturi", icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )},
    { id: "invoices" as const, label: t("settings.invoices", "Facturi"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
    { id: "email" as const, label: t("settings.email"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )},
    { id: "integrations" as const, label: t("settings.integrations"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
      </svg>
    )},
    { id: "security" as const, label: t("settings.security"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    )},
    { id: "payments" as const, label: t("settings.payments", "Plăți"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    )},
    { id: "demo" as const, label: t("settings.demo_limits", "Limite Demo"), icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )},
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-violet-50 rounded-xl">
              <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                {t("settings.title")}
              </h1>
              <p className="text-slate-500 mt-1">
                Configurează setările și preferințele platformei
              </p>
            </div>
          </div>
        </div>

        {/* Toast Notification */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-fadeIn ${
            toast.type === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"
          }`}>
            {toast.type === "success" ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="font-medium">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 hover:opacity-70">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoadingSettings && (
          <div className="text-center py-12 mb-8 bg-white rounded-2xl shadow-sm border border-slate-200">
            <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-slate-500">{t("settings.loading", "Se încarcă setările...")}</p>
          </div>
        )}

        {/* Main Content */}
        <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 ${isLoadingSettings ? "opacity-50 pointer-events-none" : ""}`}>
          {/* Tabs */}
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "text-violet-600 border-b-2 border-violet-500 bg-violet-50/50"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSave} className="p-6">
            {/* General Tab */}
            {activeTab === "general" && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-violet-50 rounded-lg">
                    <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-800">
                    Informații platformă
                  </h2>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("settings.platform_name")}
                  </label>
                  <input
                    type="text"
                    value={generalForm.platform_name}
                    onChange={(e) => setGeneralForm({ ...generalForm, platform_name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                  />
                </div>

                <ImageUpload
                  value={generalForm.platform_logo}
                  onChange={(url) => setGeneralForm({ ...generalForm, platform_logo: url })}
                  endpoint="image"
                  label={t("settings.platform_logo")}
                  maxSizeMB={5}
                  previewClassName="w-40 h-40"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.contact_email")}
                    </label>
                    <input
                      type="email"
                      value={generalForm.contact_email}
                      onChange={(e) => setGeneralForm({ ...generalForm, contact_email: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.contact_phone")}
                    </label>
                    <input
                      type="tel"
                      value={generalForm.contact_phone}
                      onChange={(e) => setGeneralForm({ ...generalForm, contact_phone: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>
                </div>

                <SupportEmailsField value={supportEmails} onChange={setSupportEmails} />

                {/* Favicon */}
                <div>
                  <ImageUpload
                    value={generalForm.favicon_url}
                    onChange={(url) => setGeneralForm({ ...generalForm, favicon_url: url })}
                    endpoint="image"
                    label={t("settings.favicon_url", "Favicon")}
                    maxSizeMB={1}
                    previewClassName="w-16 h-16"
                  />
                  <p className="text-sm text-slate-500 mt-2">{t("settings.favicon_hint", "Trage și plasează sau apasă pentru a încărca favicon (recomandat 32x32px)")}</p>
                </div>

                {/* SEO/Meta Section */}
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-cyan-50 rounded-lg">
                      <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      {t("settings.seo_settings", "SEO și meta tag-uri")}
                    </h2>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.meta_title", "Titlu meta")}
                    </label>
                    <input
                      type="text"
                      placeholder="iGROWth - Management club sportiv"
                      value={generalForm.meta_title}
                      onChange={(e) => setGeneralForm({ ...generalForm, meta_title: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="text-sm text-slate-500 mt-1">{t("settings.meta_title_hint", "Apare în filele browserului și în rezultatele căutării (recomandat 50-60 caractere)")}</p>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.meta_description", "Descriere meta")}
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Platformă pentru gestionarea cluburilor sportive, echipelor și sportivilor..."
                      value={generalForm.meta_description}
                      onChange={(e) => setGeneralForm({ ...generalForm, meta_description: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all resize-none"
                    />
                    <p className="text-sm text-slate-500 mt-1">{t("settings.meta_description_hint", "Apare în rezultatele căutării (recomandat 150-160 caractere)")}</p>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.meta_keywords", "Cuvinte cheie meta")}
                    </label>
                    <input
                      type="text"
                      placeholder="sport, management club, sportivi, antrenament"
                      value={generalForm.meta_keywords}
                      onChange={(e) => setGeneralForm({ ...generalForm, meta_keywords: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="text-sm text-slate-500 mt-1">{t("settings.meta_keywords_hint", "Listă de cuvinte cheie separate prin virgulă")}</p>
                  </div>

                  <div className="mt-4">
                    <ImageUpload
                      value={generalForm.meta_og_image}
                      onChange={(url) => setGeneralForm({ ...generalForm, meta_og_image: url })}
                      endpoint="image"
                      label={t("settings.meta_og_image", "Imagine partajare socială (imagine OG)")}
                      maxSizeMB={5}
                      previewClassName="w-64 h-36"
                    />
                    <p className="text-sm text-slate-500 mt-2">{t("settings.meta_og_image_hint", "Trage și plasează sau apasă pentru a încărca (recomandat 1200x630px)")}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Company Tab */}
            {activeTab === "company" && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-teal-50 rounded-lg">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      {t("settings.company_details", "Detalii companie")}
                    </h2>
                    <p className="text-sm text-slate-500">{t("settings.company_details_desc", "Informațiile companiei tale pentru facturi")}</p>
                  </div>
                </div>

                <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-teal-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-teal-700">
                      {t("settings.company_info", "Aceste detalii vor apărea pe toate facturile generate ca informații despre vânzător/furnizor.")}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.company_legal_name", "Denumire legală")} *
                    </label>
                    <input
                      type="text"
                      placeholder="SC COMPANY SRL"
                      value={companyForm.company_legal_name}
                      onChange={(e) => setCompanyForm({ ...companyForm, company_legal_name: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.company_cui", "CUI (Cod fiscal)")} *
                    </label>
                    <input
                      type="text"
                      placeholder="RO12345678"
                      value={companyForm.company_cui}
                      onChange={(e) => setCompanyForm({ ...companyForm, company_cui: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("settings.company_reg_number", "Număr de înregistrare")}
                  </label>
                  <input
                    type="text"
                    placeholder="J40/1234/2020"
                    value={companyForm.company_reg_number}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_reg_number: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("settings.company_address", "Adresa companiei")} *
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Str. Exemplu nr. 123, București, România"
                    value={companyForm.company_address}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_address: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all resize-none"
                  />
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-md font-semibold text-slate-800 mb-4">{t("settings.bank_details", "Detalii bancare")}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("settings.company_bank_name", "Nume bancă")}
                      </label>
                      <input
                        type="text"
                        placeholder="Banca Transilvania"
                        value={companyForm.company_bank_name}
                        onChange={(e) => setCompanyForm({ ...companyForm, company_bank_name: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("settings.company_bank_account", "Cont bancar (IBAN)")}
                      </label>
                      <input
                        type="text"
                        placeholder="RO49AAAA1B31007593840000"
                        value={companyForm.company_bank_account}
                        onChange={(e) => setCompanyForm({ ...companyForm, company_bank_account: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sports Tab */}
            {activeTab === "sports" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-50 rounded-lg">
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800">Sporturi platformă</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSportFormData({ name: "", icon: "", is_active: true });
                      setShowAddSportModal(true);
                    }}
                    className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-xl transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Adaugă sport
                  </button>
                </div>

                {loadingSports ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-slate-500">Se încarcă sporturile...</p>
                  </div>
                ) : sports.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="p-3 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-slate-500">Niciun sport configurat încă</p>
                    <p className="text-sm text-slate-400 mt-1">Adaugă sporturi pentru a permite cluburilor să își categorizeze activitățile</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sports.map((sport) => (
                      <div
                        key={sport.id}
                        className="p-4 bg-white border border-slate-200 rounded-xl hover:border-violet-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {sport.icon && (
                              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-xl">
                                {sport.icon}
                              </div>
                            )}
                            <div>
                              <h4 className="font-medium text-slate-800">{sport.name}</h4>
                              <p className="text-xs text-slate-400">Ordine: {sport.order_no}</p>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              sport.is_active
                                ? "bg-emerald-50 text-emerald-600"
                                : "bg-red-50 text-red-600"
                            }`}
                          >
                            {sport.is_active ? "Activ" : "Inactiv"}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openEditSportModal(sport)}
                            className="flex-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm transition-colors"
                          >
                            Editează
                          </button>
                          <button
                            type="button"
                            onClick={() => openDeleteSportModal(sport)}
                            className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm transition-colors"
                          >
                            Șterge
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === "invoices" && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      {t("settings.invoice_settings", "Setări facturi")}
                    </h2>
                    <p className="text-sm text-slate-500">{t("settings.invoice_settings_desc", "Configurează numerotarea și formatul facturilor")}</p>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-amber-700">
                      {t("settings.invoice_format_info", "Numerele facturilor sunt generate automat în formatul: PREFIX-AN-NUMĂR (ex: INV-2024-0001). Poți personaliza prefixul și sufixul mai jos.")}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.invoice_prefix", "Prefix factură fiscală")}
                    </label>
                    <input
                      type="text"
                      placeholder="IGR"
                      value={invoiceForm.invoice_prefix}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_prefix: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="text-sm text-slate-500 mt-1">{t("settings.invoice_prefix_hint", "Pentru facturile plătite (Factură Fiscală)")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.invoice_proforma_prefix", "Prefix proformă")}
                    </label>
                    <input
                      type="text"
                      placeholder="PRO"
                      value={invoiceForm.invoice_proforma_prefix}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_proforma_prefix: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="text-sm text-slate-500 mt-1">{t("settings.invoice_proforma_hint", "Pentru facturile în așteptare (Factură Proforma)")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.invoice_suffix", "Sufix factură")}
                    </label>
                    <input
                      type="text"
                      placeholder=""
                      value={invoiceForm.invoice_suffix}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_suffix: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="text-sm text-slate-500 mt-1">{t("settings.invoice_suffix_hint", "Text opțional după număr")}</p>
                  </div>
                </div>

                {/* Invoice Number Preview */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">{t("settings.invoice_preview", "Previzualizare")}</h4>
                  <div className="flex gap-8">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Factură Fiscală (plătită):</p>
                      <p className="text-lg font-mono text-slate-800">
                        {invoiceForm.invoice_prefix || "IGR"}-{new Date().getFullYear()}-0001{invoiceForm.invoice_suffix}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Factură Proforma (în așteptare):</p>
                      <p className="text-lg font-mono text-slate-800">
                        {invoiceForm.invoice_proforma_prefix || "PRO"}-{new Date().getFullYear()}-0001{invoiceForm.invoice_suffix}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Next Invoice Number */}
                <div className="p-4 bg-slate-100 border border-slate-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700">{t("settings.next_invoice_number", "Următorul număr de factură")}</h4>
                      <p className="text-sm text-slate-500 mt-1">{t("settings.next_invoice_hint", "Următoarea factură va folosi acest număr")}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        value={invoiceForm.invoice_next_number}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_next_number: parseInt(e.target.value) || 1 })}
                        className="w-24 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 text-center focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                      <button
                        type="button"
                        onClick={handleResetInvoiceNumber}
                        disabled={isResettingInvoice}
                        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isResettingInvoice ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            {t("common.saving", "Se salvează...")}
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {t("settings.reset_number", "Setează numărul")}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* VAT and Payment Terms */}
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-md font-semibold text-slate-800 mb-4">{t("settings.invoice_terms", "Termeni factură")}</h3>

                  {invoiceForm.company_vat_payer === null && (
                    <div className="mb-4 px-4 py-3 text-sm border rounded-xl bg-amber-50 border-amber-200 text-amber-800">
                      Statutul TVA nu este configurat. Până când alegi o variantă mai jos, generarea facturilor este
                      oprită — altfel ar pleca facturi fără TVA fără ca cineva să fi decis asta.
                    </div>
                  )}

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Statut TVA</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: true, label: "Plătitor de TVA" },
                        { value: false, label: "Neplătitor de TVA" },
                      ].map((opt) => (
                        <button
                          key={String(opt.value)}
                          type="button"
                          onClick={() => setInvoiceForm({ ...invoiceForm, company_vat_payer: opt.value })}
                          className={`px-4 py-2.5 text-sm font-semibold rounded-xl border transition-all ${
                            invoiceForm.company_vat_payer === opt.value
                              ? "bg-violet-600 border-violet-600 text-white"
                              : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Neplătitor tipărește mențiunea „Neplătitor de TVA” pe factură, în loc de o linie de TVA 0%.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("settings.invoice_vat_percent", "TVA (%)")}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="0"
                        value={invoiceForm.invoice_vat_percent}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_vat_percent: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                      <p className="text-sm text-slate-500 mt-1">{t("settings.invoice_vat_hint", "Cota aplicată pe facturi. Se folosește doar dacă ești plătitor de TVA.")}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t("settings.invoice_payment_days", "Termen de plată (zile)")}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="90"
                        placeholder="5"
                        value={invoiceForm.invoice_payment_days}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_payment_days: parseInt(e.target.value) || 5 })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                      <p className="text-sm text-slate-500 mt-1">{t("settings.invoice_payment_hint", "Numărul de zile până la scadența plății")}</p>
                    </div>
                  </div>
                </div>

                {/* Oblio — fiscal invoicing + RO e-Factura */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                      <h3 className="text-md font-semibold text-slate-800">Oblio (facturare fiscală + e-Factura)</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Cât timp e activ, Oblio emite factura fiscală și îi dă numărul. PDF-ul generat de noi rămâne
                        proformă. Trimiterea în SPV se configurează în contul Oblio.
                      </p>
                    </div>
                    <a
                      href="https://www.oblio.eu/api"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-violet-600 hover:text-violet-700 underline shrink-0"
                    >
                      Date API Oblio
                    </a>
                  </div>

                  <label className="flex items-center gap-3 mb-4 text-sm font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={invoiceForm.oblio_enabled}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, oblio_enabled: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    Emite facturile fiscale prin Oblio
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email cont Oblio</label>
                      <input
                        type="email"
                        value={invoiceForm.oblio_email}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, oblio_email: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Secret API</label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        placeholder={oblioSecretSet ? "•••••••• (salvat)" : "Secretul din contul Oblio"}
                        value={invoiceForm.oblio_secret}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, oblio_secret: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                      <p className="text-sm text-slate-500 mt-1">
                        {oblioSecretSet
                          ? "Lasă gol ca să păstrezi secretul salvat."
                          : "Nu este salvat niciun secret."}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">CIF firmă în Oblio</label>
                      <input
                        type="text"
                        placeholder="RO12345678"
                        value={invoiceForm.oblio_cif}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, oblio_cif: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Serie facturi</label>
                      <input
                        type="text"
                        list="oblio-series"
                        placeholder="ex. IGR"
                        value={invoiceForm.oblio_series_name}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, oblio_series_name: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                      <datalist id="oblio-series">
                        {oblioCheck?.series?.map((x: any) => (
                          <option key={x.name} value={x.name} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Denumire cotă TVA</label>
                      <input
                        type="text"
                        list="oblio-vat"
                        placeholder="Normala"
                        value={invoiceForm.oblio_vat_name}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, oblio_vat_name: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                      <datalist id="oblio-vat">
                        {oblioCheck?.vat_rates?.map((v: any) => (
                          <option key={v.name} value={v.name}>
                            {v.name} ({v.percent}%)
                          </option>
                        ))}
                      </datalist>
                      <p className="text-sm text-slate-500 mt-1">
                        Exact cum e scrisă în Oblio. Un nume necunoscut respinge toată factura.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Punct de lucru <span className="font-normal text-slate-400">(opțional)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Sediu"
                        value={invoiceForm.oblio_work_station}
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, oblio_work_station: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={testOblio}
                      disabled={oblioTesting}
                      className="px-4 py-2.5 text-sm font-semibold rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
                    >
                      {oblioTesting ? "Se verifică..." : "Verifică conexiunea"}
                    </button>
                    <span className="text-sm text-slate-500">
                      Salvează întâi, apoi verifică — testul folosește datele stocate.
                    </span>
                  </div>

                  {oblioCheck && (
                    <div className="mt-3 px-4 py-3 text-sm border rounded-xl bg-emerald-50 border-emerald-200 text-emerald-800">
                      Conexiune reușită. Serii disponibile:{" "}
                      {oblioCheck.series?.map((x: any) => x.name).join(", ") || "niciuna"} · Cote TVA:{" "}
                      {oblioCheck.vat_rates?.map((v: any) => `${v.name} (${v.percent}%)`).join(", ") || "niciuna"}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Email Tab - Brevo */}
            {activeTab === "email" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">
                        {t("settings.brevo_config", "Configurare Brevo")}
                      </h2>
                      <p className="text-sm text-slate-500">{t("settings.brevo_desc", "Configurează Brevo (fost Sendinblue) pentru emailuri tranzacționale")}</p>
                    </div>
                  </div>
                  <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 underline">
                    {t("settings.get_api_key", "Obține cheia API")}
                  </a>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-blue-700">
                      {t("settings.brevo_info", "Brevo oferă 300 de emailuri gratuite pe zi. Creează un cont pe brevo.com și obține cheia API din Settings > API Keys.")}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("settings.brevo_api_key", "Cheie API Brevo")} *
                  </label>
                  {hasBrevoKey && !emailForm.brevo_api_key && (
                    <div className="mb-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-emerald-700">{t("settings.api_key_configured", "Cheia API este configurată. Introdu o cheie nouă mai jos pentru a o înlocui.")}</span>
                    </div>
                  )}
                  <input
                    type="password"
                    placeholder={hasBrevoKey ? t("settings.enter_new_key", "Introdu o cheie API nouă pentru a o înlocui...") : "xkeysib-..."}
                    value={emailForm.brevo_api_key}
                    onChange={(e) => setEmailForm({ ...emailForm, brevo_api_key: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.sender_email", "Email expeditor")} *
                    </label>
                    <input
                      type="email"
                      placeholder="noreply@yourdomain.com"
                      value={emailForm.sender_email}
                      onChange={(e) => setEmailForm({ ...emailForm, sender_email: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.sender_name", "Nume expeditor")}
                    </label>
                    <input
                      type="text"
                      placeholder="iGROWth"
                      value={emailForm.sender_name}
                      onChange={(e) => setEmailForm({ ...emailForm, sender_name: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                  </div>
                </div>

                {/* Test Connection Button */}
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={testEmailConnection}
                    disabled={emailTestLoading || !emailForm.brevo_api_key}
                    className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {emailTestLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t("settings.testing", "Se testează...")}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t("settings.test_connection", "Testează conexiunea")}
                      </>
                    )}
                  </button>
                  {emailTestResult && (
                    <div className={`flex items-center gap-2 text-sm ${emailTestResult.success ? "text-emerald-600" : "text-red-600"}`}>
                      {emailTestResult.success ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      {emailTestResult.message}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === "integrations" && (
              <div className="space-y-6">
                <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">{t("settings.stripe_integration", "Integrare Stripe")}</h3>
                        <p className="text-sm text-slate-500">{t("settings.stripe_desc", "Configurează Stripe pentru procesarea plăților")}</p>
                      </div>
                    </div>
                    <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer"
                      className="text-sm text-purple-600 hover:text-purple-700 underline">
                      {t("settings.stripe_dashboard", "Panou Stripe")}
                    </a>
                  </div>

                  {/* The real state of the integration.
                      What used to be here was a Test/Live toggle that wrote a
                      column nobody read, and a "test connection" button that
                      checked the key started with "sk_" and then claimed
                      success without contacting Stripe. Both report success
                      for a test key, which is precisely the mistake this
                      screen exists to catch. */}
                  <StripeStatusPanel />

                  {/* Webhook URL */}
                  <div className="p-4 bg-slate-100 border border-slate-200 rounded-xl">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">{t("settings.webhook_url", "URL webhook")}</h4>
                    <p className="text-xs text-slate-500 mb-2">{t("settings.webhook_info", "Adaugă acest URL în setările de webhook Stripe")}</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-mono break-all">
                        {getWebhookUrl()}
                      </code>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(getWebhookUrl());
                          alert(t("settings.copied", "Copiat în clipboard!"));
                        }}
                        className="p-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors"
                        title={t("settings.copy", "Copiază")}
                      >
                        <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Lives purchase */}
                  <div className="mt-6 p-4 bg-white border border-slate-200 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">
                          {t("settings.lives_purchase", "Cumpărare vieți")}
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {t(
                            "settings.lives_purchase_desc",
                            "Sportivii adulți (sau părinții pentru minori) pot cumpăra vieți."
                          )}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={integrationsForm.lives_purchase_enabled}
                          onChange={(e) =>
                            setIntegrationsForm({ ...integrationsForm, lives_purchase_enabled: e.target.checked })
                          }
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                      </label>
                    </div>
                    {/* Packages manager */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-slate-600">Pachete (preț cu TVA inclus)</p>
                        <button
                          type="button"
                          onClick={addPackage}
                          className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-700"
                        >
                          + Adaugă pachet
                        </button>
                      </div>
                      {lifePackages.length === 0 ? (
                        <p className="text-xs text-slate-400">Niciun pachet configurat.</p>
                      ) : (
                        <div className="space-y-2">
                          <div className="grid grid-cols-12 gap-2 px-1 text-[11px] font-medium text-slate-400">
                            <span className="col-span-3">Vieți</span>
                            <span className="col-span-4">Preț</span>
                            <span className="col-span-2">Activ</span>
                            <span className="col-span-3 text-right">Acțiuni</span>
                          </div>
                          {lifePackages.map((p) => (
                            <div key={p.id} className="grid grid-cols-12 gap-2 items-center">
                              <input
                                type="number"
                                min={1}
                                max={60}
                                value={p.qty}
                                onChange={(e) => patchPkgLocal(p.id, { qty: parseInt(e.target.value, 10) || 0 })}
                                className="col-span-3 px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white text-slate-800"
                              />
                              <div className="col-span-4 flex items-center gap-1">
                                <input
                                  type="number"
                                  min={0}
                                  step={0.5}
                                  value={(p.price_cents / 100).toString()}
                                  onChange={(e) =>
                                    patchPkgLocal(p.id, {
                                      price_cents: Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)),
                                    })
                                  }
                                  className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white text-slate-800"
                                />
                                <span className="text-xs text-slate-500 uppercase">{p.currency}</span>
                              </div>
                              <div className="col-span-2">
                                <input
                                  type="checkbox"
                                  checked={p.active ?? true}
                                  onChange={(e) => patchPkgLocal(p.id, { active: e.target.checked })}
                                  className="w-4 h-4"
                                />
                              </div>
                              <div className="col-span-3 flex justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => savePackage(p)}
                                  className="px-2 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                                >
                                  Salvează
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removePackage(p.id)}
                                  className="px-2 py-1 rounded-lg bg-rose-100 text-rose-700 text-xs font-semibold hover:bg-rose-200"
                                >
                                  Șterge
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-slate-400">
                        Prețul per viață se calculează automat. Plafon: maxim 60 de vieți per cont.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === "security" && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-50 rounded-lg">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-800">
                    Setări securitate
                  </h2>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("settings.password_min_length")}
                  </label>
                  <input
                    type="number"
                    min="6"
                    max="32"
                    value={securityForm.password_min_length}
                    onChange={(e) => setSecurityForm({ ...securityForm, password_min_length: parseInt(e.target.value) || 8 })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                  />
                </div>

                <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <input
                    type="checkbox"
                    id="require_special_char"
                    checked={securityForm.require_special_char}
                    onChange={(e) => setSecurityForm({ ...securityForm, require_special_char: e.target.checked })}
                    className="w-5 h-5 text-violet-500 bg-white border-slate-300 rounded focus:ring-violet-500 focus:ring-2"
                  />
                  <label htmlFor="require_special_char" className="ml-3 text-sm font-medium text-slate-700">
                    {t("settings.require_special_char")}
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("settings.session_timeout")}
                  </label>
                  <input
                    type="number"
                    min="15"
                    value={securityForm.session_timeout}
                    onChange={(e) => setSecurityForm({ ...securityForm, session_timeout: parseInt(e.target.value) || 120 })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                  />
                  <p className="mt-2 text-sm text-slate-500">Durata sesiunii în minute</p>
                </div>
              </div>
            )}

            {activeTab === "payments" && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-emerald-50">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      {t("settings.payments", "Plăți")}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {t(
                        "settings.paymentsDesc",
                        "Încasarea cotizațiilor cluburilor de la familii. Nu are legătură cu abonamentele platformei, care funcționează independent."
                      )}
                    </p>
                  </div>
                </div>

                <div className="p-4 border border-amber-200 bg-amber-50 rounded-xl">
                  <p className="text-sm text-amber-900">
                    {t(
                      "settings.paymentsWarning",
                      "Cât timp comutatorul principal e oprit, nu se generează perioade de plată, nu se trimite nicio notificare despre bani și nu se debitează niciun card. Aplicația se comportă exact ca inainte de acest modul."
                    )}
                  </p>
                </div>

                {/* The master switch, apart from the rest. */}
                <label className="flex items-start gap-3 p-4 border cursor-pointer rounded-xl border-slate-200">
                  <input
                    type="checkbox"
                    checked={paymentsForm.payments_module_enabled}
                    onChange={(e) =>
                      setPaymentsForm({ ...paymentsForm, payments_module_enabled: e.target.checked })
                    }
                    className="w-5 h-5 mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-600">
                    <span className="block font-semibold text-slate-800">
                      {t("settings.paymentsMaster", "Activează modulul de plăți")}
                    </span>
                    {t(
                      "settings.paymentsMasterHint",
                      "Comutatorul principal. Cât timp e oprit, tot ce urmează nu face nimic, indiferent cum e bifat."
                    )}
                  </span>
                </label>

                <div
                  className={`space-y-3 transition-opacity ${
                    paymentsForm.payments_module_enabled ? "" : "opacity-50 pointer-events-none"
                  }`}
                >
                  {(
                    [
                      [
                        "payments_online_enabled",
                        t("settings.paymentsOnline", "Plată cu cardul în aplicație"),
                        t(
                          "settings.paymentsOnlineHint",
                          "Clubul se conectează la Stripe și încasează în contul lui. Include chitanțele, restituirile și reconcilierea zilnică."
                        ),
                      ],
                      [
                        "payments_reminders_enabled",
                        t("settings.paymentsReminders", "Memento-uri de plată"),
                        t(
                          "settings.paymentsRemindersHint",
                          "Scara de notificări la −3, 0, +1, +3, +5 și +7 zile. Merge și fără plata cu cardul: un club care încasează numerar vrea totuși familiile anunțate."
                        ),
                      ],
                      [
                        "payments_auto_debit_enabled",
                        t("settings.paymentsAutoDebit", "Plată automată cu card salvat"),
                        t(
                          "settings.paymentsAutoDebitHint",
                          "Necesită plata cu cardul. Fiecare debitare e anunțată cu 3 zile înainte, iar părintele o poate opri oricând."
                        ),
                      ],
                      [
                        "payments_discounts_enabled",
                        t("settings.paymentsDiscounts", "Reduceri, burse și scutiri"),
                        t(
                          "settings.paymentsDiscountsHint",
                          "Reducerea pentru frați la nivel de club și scutirile pe sportiv."
                        ),
                      ],
                      [
                        "payments_instalments_enabled",
                        t("settings.paymentsInstalments", "Plată în rate"),
                        t("settings.paymentsInstalmentsHint", "Eșalonarea unei perioade în rate stabilite de club."),
                      ],
                    ] as const
                  ).map(([key, label, hint]) => {
                    const needsOnline = key === "payments_auto_debit_enabled";
                    const blocked = needsOnline && !paymentsForm.payments_online_enabled;
                    return (
                      <label
                        key={key}
                        className={`flex items-start gap-3 p-4 border rounded-xl border-slate-200 ${
                          blocked ? "opacity-50" : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={blocked}
                          checked={!blocked && paymentsForm[key]}
                          onChange={(e) => setPaymentsForm({ ...paymentsForm, [key]: e.target.checked })}
                          className="w-5 h-5 mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                        />
                        <span className="text-sm text-slate-600">
                          <span className="block font-semibold text-slate-800">{label}</span>
                          {hint}
                          {blocked && (
                            <span className="block mt-1 text-xs font-semibold text-amber-700">
                              {t("settings.needsOnline", "Necesită plata cu cardul, activată mai sus.")}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="p-4 border rounded-xl border-slate-200 bg-slate-50">
                  <p className="text-sm font-semibold text-slate-700">
                    {t("settings.paymentsOrder", "Ordinea recomandată la pornire")}
                  </p>
                  <ol className="mt-2 ml-4 space-y-1 text-sm list-decimal text-slate-600">
                    <li>
                      {t(
                        "settings.paymentsStep1",
                        "Doar comutatorul principal: perioadele de plată încep să se genereze și cluburile văd corect ce au de încasat, fără ca nimeni să fie notificat."
                      )}
                    </li>
                    <li>{t("settings.paymentsStep2", "Memento-uri, după ce verifici că sumele și scadențele sunt corecte.")}</li>
                    <li>{t("settings.paymentsStep3", "Plata cu cardul, cu un club de test în Stripe test mode.")}</li>
                    <li>{t("settings.paymentsStep4", "Reduceri, rate și plata automată, la final.")}</li>
                  </ol>
                </div>
              </div>
            )}

            {activeTab === "demo" && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                      {t("settings.demo_limits", "Limite Demo")}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {t("settings.demo_limits_desc", "Pragurile aplicate conturilor înregistrate prin self-service în perioada de trial.")}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-900 space-y-1">
                      <p>{t("settings.demo_limits_info_1", "Aceste limite se aplică numai conturilor demo (înregistrate prin /register-club).")}</p>
                      <p>{t("settings.demo_limits_info_2", "Conturile cu status active nu au limite. Pentru a transforma instant un cont demo într-unul activ și fără limite, folosește butonul „Activează clubul\" din pagina de detalii a clubului.")}</p>
                      <p>{t("settings.demo_limits_info_3", "Numărătoarea ia în considerare doar antrenorii și sportivii activi — dezactivarea unui cont eliberează imediat slotul.")}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.demo_max_clubs", "Cluburi per cont demo")}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={demoForm.demo_max_clubs}
                      onChange={(e) => setDemoForm({ ...demoForm, demo_max_clubs: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="mt-2 text-xs text-slate-500">{t("settings.demo_max_clubs_hint", "Default: 1")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.demo_max_coaches", "Antrenori activi per club demo")}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={demoForm.demo_max_coaches}
                      onChange={(e) => setDemoForm({ ...demoForm, demo_max_coaches: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="mt-2 text-xs text-slate-500">{t("settings.demo_max_coaches_hint", "Default: 3")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("settings.demo_max_athletes", "Sportivi activi per club demo")}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={demoForm.demo_max_athletes}
                      onChange={(e) => setDemoForm({ ...demoForm, demo_max_athletes: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    />
                    <p className="mt-2 text-xs text-slate-500">{t("settings.demo_max_athletes_hint", "Default: 30")}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-3 bg-violet-500 hover:bg-violet-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t("common.loading")}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t("common.save")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Add Sport Modal */}
        {showAddSportModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-slate-800">Adaugă sport nou</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nume sport *</label>
                  <input
                    type="text"
                    value={sportFormData.name}
                    onChange={(e) => setSportFormData({ ...sportFormData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    placeholder="ex: Fotbal, Baschet"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Iconiță (Emoji)</label>
                  <input
                    type="text"
                    value={sportFormData.icon}
                    onChange={(e) => setSportFormData({ ...sportFormData, icon: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    placeholder="ex: ⚽"
                  />
                </div>

                <div className="flex items-center p-3 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="add-sport-active"
                    checked={sportFormData.is_active}
                    onChange={(e) => setSportFormData({ ...sportFormData, is_active: e.target.checked })}
                    className="w-5 h-5 text-violet-500 bg-white border-slate-300 rounded focus:ring-violet-500"
                  />
                  <label htmlFor="add-sport-active" className="ml-3 text-sm font-medium text-slate-700">
                    Activ
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddSportModal(false);
                    setSportFormData({ name: "", icon: "", is_active: true });
                  }}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={handleAddSport}
                  disabled={!sportFormData.name.trim()}
                  className="flex-1 px-4 py-3 bg-violet-500 hover:bg-violet-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Adaugă sport
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Sport Modal */}
        {showEditSportModal && selectedSport && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-slate-800">Editează sport</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nume sport *</label>
                  <input
                    type="text"
                    value={sportFormData.name}
                    onChange={(e) => setSportFormData({ ...sportFormData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Iconiță (Emoji)</label>
                  <input
                    type="text"
                    value={sportFormData.icon}
                    onChange={(e) => setSportFormData({ ...sportFormData, icon: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                  />
                </div>

                <div className="flex items-center p-3 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="edit-sport-active"
                    checked={sportFormData.is_active}
                    onChange={(e) => setSportFormData({ ...sportFormData, is_active: e.target.checked })}
                    className="w-5 h-5 text-violet-500 bg-white border-slate-300 rounded focus:ring-violet-500"
                  />
                  <label htmlFor="edit-sport-active" className="ml-3 text-sm font-medium text-slate-700">
                    Activ
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditSportModal(false);
                    setSelectedSport(null);
                    setSportFormData({ name: "", icon: "", is_active: true });
                  }}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={handleEditSport}
                  disabled={!sportFormData.name.trim()}
                  className="flex-1 px-4 py-3 bg-violet-500 hover:bg-violet-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Salvează modificările
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Sport Modal */}
        {showDeleteSportModal && selectedSport && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-50 rounded-lg">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-slate-800">Șterge sport</h2>
              </div>
              <p className="text-slate-600 mb-6">
                Sigur vrei să ștergi <span className="font-semibold text-slate-800">{selectedSport.name}</span>?
                Această acțiune nu poate fi anulată.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteSportModal(false);
                    setSelectedSport(null);
                  }}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={handleDeleteSport}
                  className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors"
                >
                  Șterge
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
